import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoAmbienteManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    codigo_tipo_ambiente: '',
    descr_tp_ambiente: '',
    id_class_amb: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [classificacoes, setClassificacoes] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);
  const [proximoCodigo, setProximoCodigo] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchClassificacoes = useCallback(async () => {
    try {
      const response = await api.get('/tipo-class-ambiente');
      setClassificacoes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar classificações: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-ambiente');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      if (error.response && error.response.status === 403) {
        mostrarMensagem('error', 'Você não tem permissão para acessar este recurso.');
      } else {
        mostrarMensagem('error', `Erro de conexão: ${error.message}`);
      }
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-ambiente');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const fetchNextCode = useCallback(async () => {
    try {
      const response = await api.get('/tipo-ambiente/last-code');
      const lastCode = response.data.data;
      console.log('Último código recebido:', lastCode, typeof lastCode);

      let nextCode;
      if (lastCode !== null && lastCode !== undefined) {
        nextCode = parseInt(lastCode, 10) + 1;
      } else {
        nextCode = 1;
      }

      if (isNaN(nextCode)) {
        console.error('Erro: Próximo código calculado é NaN. LastCode:', lastCode);
        nextCode = 1;
      }

      console.log('Sugerindo próximo código:', nextCode);
      setFormData(prev => ({ ...prev, codigo_tipo_ambiente: String(nextCode) }));
      setProximoCodigo(nextCode);
    } catch (error) {
      console.error('Erro ao buscar próximo código:', error);
    }
  }, []);

  useEffect(() => {
    verificarAPI();
    fetchClassificacoes();
    carregarDados();
    fetchNextCode();
  }, [verificarAPI, fetchClassificacoes, carregarDados, fetchNextCode]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'tipo de ambiente', { id, dadosAtualizacao });

    try {
      const response = await api.put(`/tipo-ambiente/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'tipo de ambiente', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
        carregarDados(false);
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'tipo de ambiente', { id });

    try {
      const response = await api.delete(`/tipo-ambiente/${id}`);
      logarSucesso('Excluir', 'tipo de ambiente', response.data);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
      carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const colunas = useMemo(() => {
    const classificacoesMap = classificacoes.reduce((acc, classificacao) => {
      acc[classificacao.id_class_amb] = classificacao.classificacao;
      return acc;
    }, {});

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_tp_ambiente, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_tp_ambiente}"?`)) {
            excluirRegistro(data.id_tp_ambiente);
          }
        },
        headerSort: false
      },
      {
        title: 'ID',
        field: 'id_tp_ambiente',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Código',
        field: 'codigo_tipo_ambiente',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Código...',
        sorter: 'number',
        editor: 'input'
      },
      {
        title: 'Descrição',
        field: 'descr_tp_ambiente',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Classificação',
        field: 'id_class_amb',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Classe...',
        sorter: 'string',
        formatter: (cell) => {
          return classificacoesMap[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeClassificacao = classificacoesMap[rowValue] || rowValue || '';
          return nomeClassificacao.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: classificacoesMap
        }
      }
    ];
  }, [classificacoes, atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_tp_ambiente'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { codigo_tipo_ambiente, descr_tp_ambiente, id_class_amb } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (codigo_tipo_ambiente === '') {
      camposObrigatorios.push('Código do Tipo');
      erros.push('codigo_tipo_ambiente');
    }

    // Validação por Regex (apenas números)
    const codeRegex = /^\d+$/;
    if (codigo_tipo_ambiente !== '' && !codeRegex.test(codigo_tipo_ambiente.toString())) {
      setCamposComErro(['codigo_tipo_ambiente']);
      const sugestao = proximoCodigo ? `. Sugestão: ${proximoCodigo}` : '';
      mostrarMensagem('error', `O código do tipo de ambiente deve ser numérico${sugestao}`);
      return;
    }

    if (!descr_tp_ambiente.trim()) {
      camposObrigatorios.push('Descrição');
      erros.push('descr_tp_ambiente');
    }
    if (!id_class_amb.trim()) {
      camposObrigatorios.push('Classificação');
      erros.push('id_class_amb');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      codigo_tipo_ambiente: formData.codigo_tipo_ambiente,
      descr_tp_ambiente: formData.descr_tp_ambiente,
      id_class_amb: formData.id_class_amb
    };

    logarRequisicao('Adicionar', 'tipo de ambiente', dataToSend);

    try {
      setLoading(true);
      const response = await api.post('/tipo-ambiente', dataToSend);
      logarSucesso('Adicionar', 'tipo de ambiente', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ codigo_tipo_ambiente: '', descr_tp_ambiente: '', id_class_amb: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      fetchNextCode();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de ambiente');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipos_ambiente.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Ambiente</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tipo_de_ambiente</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/tipos de ambiente.mov" buttonText="Ver Tutorial Tipos de Ambiente" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="codigo_tipo_ambiente">Código do tipo:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="number"
              id="codigo_tipo_ambiente"
              name="codigo_tipo_ambiente"
              value={formData.codigo_tipo_ambiente}
              onChange={handleInputChange}
              placeholder="Ex: 1"
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('codigo_tipo_ambiente') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="descr_tp_ambiente">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="descr_tp_ambiente"
              name="descr_tp_ambiente"
              value={formData.descr_tp_ambiente}
              onChange={handleInputChange}
              placeholder="Ex: Sala de Aula"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('descr_tp_ambiente') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="id_class_amb">Classificação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_class_amb"
              name="id_class_amb"
              value={formData.id_class_amb}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_class_amb') ? 'input-error' : ''}
            >
              <option value="">Selecione uma classificação</option>
              {classificacoes.map(c => (
                <option key={c.id_class_amb} value={c.id_class_amb}>{c.classificacao}</option>
              ))}
            </select>
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoAmbienteManager;